import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, AlertCircle, ArrowLeft } from "lucide-react";
import axios from "axios";
import { NOOR } from "@/constants/testIds/noor";
import { fadeUp, trans } from "@/components/noor/motion";
import WaitingRoom from "@/components/noor/WaitingRoom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  email: "",
  city: "",
  intro: "",
  referral_code: "",
};

function PageShell({ children }) {
  return (
    <main
      data-testid={NOOR.innerCirclePage}
      className="min-h-screen pt-24 pb-24"
      style={{ background: "var(--noor-obsidian)" }}
    >
      <div className="grain-overlay" />
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-xs"
          style={{ color: "var(--noor-vellum)", letterSpacing: "0.28em", textTransform: "uppercase" }}
        >
          <ArrowLeft size={14} />
          Back to the house
        </Link>
        {children}
      </div>
    </main>
  );
}

function CodeGateIntro() {
  return (
    <div className="col-span-12 lg:col-span-5">
      <p className="overline mb-6">The Inner Circle</p>
      <h1
        className="font-display leading-[0.95] tracking-[-0.03em]"
        style={{ color: "var(--noor-bone)", fontSize: "clamp(3rem, 6vw, 6rem)" }}
      >
        By <em className="italic" style={{ color: "var(--noor-rosegold)" }}>invitation</em><br />
        only.
      </h1>
    </div>
  );
}

function CodeGateInput({ code, onChange, status }) {
  return (
    <div className="flex items-stretch gap-4">
      <input
        data-testid={NOOR.innerCircleCodeInput}
        type="text"
        required
        placeholder="NOOR-XXXX"
        value={code}
        onChange={onChange}
        className="input-line font-mono uppercase"
        style={{ letterSpacing: "0.28em" }}
        disabled={status === "loading"}
      />
      <button
        data-testid={NOOR.innerCircleCodeSubmit}
        type="submit"
        className="btn-solid whitespace-nowrap"
        disabled={status === "loading"}
      >
        {status === "loading" ? "…" : "Enter"}
        <ArrowRight size={14} />
      </button>
    </div>
  );
}

function CodeGateError({ err }) {
  return (
    <p
      data-testid={NOOR.innerCircleCodeError}
      className="mt-6 inline-flex items-center gap-2 text-sm"
      style={{ color: "#E68B7A" }}
    >
      <AlertCircle size={14} /> {err}
    </p>
  );
}

// Hook owns the gate-submission lifecycle. Mirrors the application form pattern.
function useCodeGate(onPass) {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErr("");
    try {
      const res = await axios.post(`${API}/inner-circle/code`, { code });
      if (res.data?.valid) {
        onPass(code.trim().toUpperCase());
      } else {
        setStatus("error");
        setErr("That key does not open this door.");
      }
    } catch {
      // Network failure — same UX as an invalid code (don't leak transport
      // details to a public-facing gate). Keepers see real errors in /admin.
      setStatus("error");
      setErr("Something went wrong. Please try again.");
    }
  };

  const onInputChange = (e) => {
    setCode(e.target.value);
    if (status === "error") {
      setStatus("idle");
      setErr("");
    }
  };

  return { code, status, err, submit, onInputChange };
}

function CodeGate({ onPass }) {
  const { code, status, err, submit, onInputChange } = useCodeGate(onPass);

  return (
    <section className="grid grid-cols-12 gap-y-10 md:gap-10 mt-24">
      <CodeGateIntro />
      <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
        <p className="text-base md:text-lg font-light leading-relaxed max-w-md" style={{ color: "var(--noor-vellum)" }}>
          Edition Nº 001 of NOOR is released only to members of the Inner Circle.
          If a keeper of the house has shared a key with you, enter it below.
        </p>
        <form onSubmit={submit} className="mt-12 max-w-md">
          <label className="overline-vellum block mb-3">Invitation Key</label>
          <CodeGateInput code={code} onChange={onInputChange} status={status} />
          {status === "error" && <CodeGateError err={err} />}
          <p className="mt-10 font-mono text-xs" style={{ color: "var(--noor-vellum)", letterSpacing: "0.2em" }}>
            Without a key? <Link to="/" className="underline" style={{ color: "var(--noor-rosegold)" }}>Leave a letter at the door.</Link>
          </p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="overline-vellum block mb-3">{label}</label>
      {children}
      {hint && (
        <p className="font-mono text-[10px] mt-2" style={{ color: "var(--noor-vellum)", letterSpacing: "0.2em" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function ApplicationSuccess() {
  const navigate = useNavigate();
  return (
    <section className="mt-24 max-w-2xl mx-auto text-center">
      <motion.div {...fadeUp} transition={trans(1)}>
        <p className="overline mb-6">Your Letter Has Reached the House</p>
        <h2
          data-testid={NOOR.innerCircleApplySuccess}
          className="font-display leading-[1] tracking-[-0.02em]"
          style={{ color: "var(--noor-bone)", fontSize: "clamp(2.4rem, 5vw, 4.4rem)" }}
        >
          A keeper will <em className="italic" style={{ color: "var(--noor-rosegold)" }}>respond</em><br />
          within 72 hours.
        </h2>
        <p className="mt-10 text-base font-light max-w-md mx-auto" style={{ color: "var(--noor-vellum)" }}>
          Watch for a personal note from the cellar. Should your application meet the keepers&rsquo; favour,
          a private invitation to claim a bottle of Edition Nº 001 will follow.
        </p>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="btn-ghost mt-12"
        >
          Return to the house
        </button>
      </motion.div>
    </section>
  );
}

function ApplicationIntro() {
  return (
    <div className="col-span-12 lg:col-span-5">
      <p className="overline mb-6">Application · Edition Nº 001</p>
      <h1
        className="font-display leading-[0.95] tracking-[-0.03em]"
        style={{ color: "var(--noor-bone)", fontSize: "clamp(2.8rem, 5.4vw, 5.6rem)" }}
      >
        Tell the keepers<br />
        <em className="italic" style={{ color: "var(--noor-rosegold)" }}>how you intend</em><br />
        to meet NOOR.
      </h1>
      <p className="mt-10 text-base font-light max-w-sm" style={{ color: "var(--noor-vellum)" }}>
        The house responds to every letter, though sometimes by candlelight.
        There is no checkout. There is no waitlist. There is only this: write to us, and we will write back.
      </p>
    </div>
  );
}

function ApplicantIdentityFields({ form, onChange }) {
  return (
    <>
      <Field label="Your full name">
        <input
          data-testid={NOOR.innerCircleApplyFullName}
          type="text"
          required
          value={form.full_name}
          onChange={onChange("full_name")}
          placeholder="Anaïs Devereaux"
          className="input-line"
        />
      </Field>
      <Field label="Email">
        <input
          data-testid={NOOR.innerCircleApplyEmail}
          type="email"
          required
          value={form.email}
          onChange={onChange("email")}
          placeholder="anais@maison.com"
          className="input-line"
        />
      </Field>
      <Field label="City · Country" hint="Where would NOOR find you?">
        <input
          data-testid={NOOR.innerCircleApplyCity}
          type="text"
          required
          value={form.city}
          onChange={onChange("city")}
          placeholder="Mexico City · México"
          className="input-line"
        />
      </Field>
    </>
  );
}

function ApplicantStoryFields({ form, onChange }) {
  return (
    <>
      <Field
        label="Two sentences — how you intend to meet NOOR"
        hint="Be brief. The keepers value clarity."
      >
        <textarea
          data-testid={NOOR.innerCircleApplyIntro}
          required
          rows={4}
          value={form.intro}
          onChange={onChange("intro")}
          placeholder="Tell us in two sentences."
          className="input-line resize-none"
        />
      </Field>
      <Field label="Invitation key" hint="Auto-filled from the door. Optional referrals welcome.">
        <input
          data-testid={NOOR.innerCircleApplyReferral}
          type="text"
          value={form.referral_code}
          onChange={onChange("referral_code")}
          placeholder="NOOR-XXXX"
          className="input-line font-mono uppercase"
          style={{ letterSpacing: "0.28em" }}
        />
      </Field>
    </>
  );
}

function ApplicationSubmitRow({ status, err }) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-4">
      <button
        data-testid={NOOR.innerCircleApplySubmit}
        type="submit"
        className="btn-solid"
        disabled={status === "loading"}
      >
        {status === "loading" ? "Sending…" : "Send the letter"}
        <ArrowRight size={14} />
      </button>
      {status === "error" && (
        <p className="inline-flex items-center gap-2 text-sm" style={{ color: "#E68B7A" }}>
          <AlertCircle size={14} /> {err}
        </p>
      )}
    </div>
  );
}

function Application({ unlockedCode }) {
  const [form, setForm] = useState({ ...INITIAL, referral_code: unlockedCode || "" });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (status === "error") {
      setStatus("idle");
      setErr("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErr("");
    try {
      await axios.post(`${API}/inner-circle/apply`, form);
      setStatus("ok");
    } catch (e2) {
      setStatus("error");
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "ok") return <ApplicationSuccess />;

  return (
    <section className="mt-24 grid grid-cols-12 gap-y-10 md:gap-10">
      <ApplicationIntro />
      <form onSubmit={submit} className="col-span-12 lg:col-span-6 lg:col-start-7 space-y-10">
        <ApplicantIdentityFields form={form} onChange={set} />
        <ApplicantStoryFields form={form} onChange={set} />
        <ApplicationSubmitRow status={status} err={err} />
      </form>
    </section>
  );
}

export default function InnerCircle() {
  const [unlocked, setUnlocked] = useState(false);
  const [unlockedCode, setUnlockedCode] = useState("");
  const [admitted, setAdmitted] = useState(false);

  const onPass = (code) => {
    setUnlocked(true);
    setUnlockedCode(code);
  };

  return (
    <PageShell>
      {!unlocked && <CodeGate onPass={onPass} />}
      {unlocked && !admitted && (
        <WaitingRoom onAdmitted={() => setAdmitted(true)}>
          <Application unlockedCode={unlockedCode} />
        </WaitingRoom>
      )}
      {unlocked && admitted && <Application unlockedCode={unlockedCode} />}
    </PageShell>
  );
}
